import { Injectable, computed, inject, signal } from '@angular/core';
import type { HubConnection } from '@microsoft/signalr';
import { AuthService } from '@nabotavle/auth';
import {
  EMERGENCY_CHANGED_EVENT,
  EMERGENCY_HUB_PATH,
  EmergencyService,
  type BeredskapItemView,
  type EmergencyAlertView,
} from '@nabotavle/data-access/core';

/**
 * Phase V1.11 — the live nødvarsel bridge. Owns the single SignalR connection to `/hubs/emergency`
 * (JWT via the access-token factory) and exposes the current active alert as a signal that drives the
 * global resident takeover. On start it seeds the alert (GET /active) + the beredskap assembly point,
 * then the hub pushes every change (trigger → alert; resolve → null). Zoneless/OnPush friendly.
 *
 * `@microsoft/signalr` (56 kB raw) is loaded with a dynamic import so it stays out of the initial
 * bundle: this store is eager (the shell starts it), the socket is not needed for first paint, and
 * GET /active has already seeded the state by the time the chunk arrives.
 */
@Injectable({ providedIn: 'root' })
export class EmergencyStore {
  private readonly auth = inject(AuthService);
  private readonly api = inject(EmergencyService);
  private connection: HubConnection | null = null;
  private starting = false;

  /** The current active alert, or null. Drives the global takeover overlay. */
  readonly currentAlert = signal<EmergencyAlertView | null>(null);
  /** Beredskap assembly point (oppmøteplass), surfaced on the takeover. */
  readonly assemblyPoint = signal<BeredskapItemView | null>(null);

  readonly hasActive = computed(() => this.currentAlert() !== null);

  /** Start the connection once (idempotent). Safe to call from the shell constructor. */
  ensureStarted(): void {
    if (this.connection || this.starting) {
      return;
    }
    this.starting = true;

    // Seed the current state immediately (don't wait for the socket).
    this.api.active().subscribe({ next: (a) => this.currentAlert.set(a), error: () => { /* best-effort seed */ } });
    this.api.beredskap().subscribe({
      next: (b) => this.assemblyPoint.set(b.items.find((i) => i.kind === 'assemblyPoint') ?? null),
      error: () => { /* best-effort seed */ },
    });

    void this.connect();
  }

  private async connect(): Promise<void> {
    let signalr: typeof import('@microsoft/signalr');
    try {
      signalr = await import('@microsoft/signalr');
    } catch {
      this.starting = false;
      return; // live channel is optional; GET /active already seeded
    }
    const connection = new signalr.HubConnectionBuilder()
      .withUrl(EMERGENCY_HUB_PATH, { accessTokenFactory: () => this.auth.accessToken() ?? '' })
      .withAutomaticReconnect()
      .configureLogging(signalr.LogLevel.Warning)
      .build();

    connection.on(EMERGENCY_CHANGED_EVENT, (a: EmergencyAlertView | null) => this.currentAlert.set(a));
    // On (re)connect, re-seed in case a change was missed while offline.
    connection.onreconnected(() => this.refresh());

    this.connection = connection;
    this.starting = false;
    connection.start().catch(() => { /* live channel is optional; GET /active already seeded */ });
  }

  /** Acknowledge the active alert ("Jeg er i sikkerhet") — optimistic, then server-confirmed. */
  acknowledge(alertId: string): void {
    const current = this.currentAlert();
    if (current && current.id === alertId) {
      this.currentAlert.set({ ...current, iAcked: true, ackCount: current.ackCount + (current.iAcked ? 0 : 1) });
    }
    this.api.ack(alertId).subscribe({ error: () => this.refresh() });
  }

  private refresh(): void {
    this.api.active().subscribe({ next: (a) => this.currentAlert.set(a), error: () => { /* keep last state */ } });
  }
}
